/**
 * Gets the status of an inventory item based on stock levels
 *
 * @param {Object} item - Inventory item object
 * @returns {string} Status: 'Out of Stock', 'Low Stock', or 'In Stock'
 */
export const getItemStatus = (item) => {
  const inventory = item.inventoryInfo || {};
  const stock = inventory.stock || item.stock || 0;
  const minStock = inventory.minStockLevel || 0;

  if (stock <= 0) {
    return 'Out of Stock';
  } else if (stock <= minStock) {
    return 'Low Stock';
  }
  return 'In Stock';
};

const normalizeBarcodes = (item) => {
  const inventory = item.inventoryInfo || {};
  const raw =
    item.barcodeNumbers ??
    inventory.barcodeNumbers ??
    inventory.barcodeNumber ??
    item.barcodeNumber ??
    [];

  if (Array.isArray(raw)) return raw;
  if (raw == null || raw === '') return [];
  return [raw];
};

/**
 * Filters inventory items based on search query and status filter
 * Searches across all item fields: name, category, price, stock, SKU, unit, status, and barcodes
 *
 * @param {Array} items - Array of inventory items to filter
 * @param {string} searchQuery - Search query string
 * @param {string|Array} statusFilter - Status filter: 'All', array of statuses, or single status string
 * @returns {Array} Filtered array of items
 */
export const filterInventoryItems = (
  items,
  searchQuery,
  statusFilter = 'All'
) => {
  let filtered = items;

  // Apply status filter
  if (statusFilter && statusFilter !== 'All') {
    // Handle array of statuses (multi-select)
    if (Array.isArray(statusFilter)) {
      // If empty array, show all (no filter applied)
      if (statusFilter.length === 0) {
        // Show all - no filtering needed
      } else {
        // Check if all statuses are selected (equivalent to "All")
        const allStatusValues = ['Out of Stock', 'Low Stock', 'In Stock'];
        const isAllSelected =
          statusFilter.length === allStatusValues.length &&
          allStatusValues.every((status) => statusFilter.includes(status));

        if (!isAllSelected) {
          // Filter by selected statuses
          filtered = filtered.filter((item) => {
            const status = getItemStatus(item);
            return statusFilter.includes(status);
          });
        }
        // If all selected, show all (no filtering needed)
      }
    } else {
      // Handle single status string
      filtered = filtered.filter((item) => {
        const status = getItemStatus(item);
        return status === statusFilter;
      });
    }
  }

  // Apply search query filter
  if (!searchQuery) return filtered;

  const q = searchQuery.toLowerCase();

  return filtered.filter((item) => {
    const inventory = item.inventoryInfo || {};
    const barcodes = normalizeBarcodes(item);
    const categoryName =
      typeof item.category === 'object'
        ? item.category?.name || ''
        : item.category || '';
    const stock = inventory.stock || item.stock || 0;
    const price = item.price || 0;
    const unit = inventory.unit || item.unit || '';
    const sku = inventory.sku || '';
    const status = getItemStatus(item);

    // Search in all fields including status
    return (
      item.name?.toLowerCase().includes(q) ||
      categoryName?.toLowerCase().includes(q) ||
      String(price).includes(q) ||
      String(stock).includes(q) ||
      String(sku).toLowerCase().includes(q) ||
      String(unit).toLowerCase().includes(q) ||
      status.toLowerCase().includes(q) ||
      barcodes.some((barcode) =>
        String(barcode || '')
          .toLowerCase()
          .includes(q)
      )
    );
  });
};
